import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowRight, Code2, Lightbulb, RefreshCw } from 'lucide-react'
import { ROUTES, productPath } from '@/lib/constants'
import { PRODUCTS } from '@/lib/products'
import Reveal from '@/components/shared/Reveal'
import SectionHeader from '@/components/shared/SectionHeader'
import CtaBand from '@/components/shared/CtaBand'
import { ProductIconTile } from '@/components/shared/ProductBits'

const PILLARS = [
  { key: 'think', icon: Lightbulb },
  { key: 'develop', icon: Code2 },
  { key: 'remaster', icon: RefreshCw },
] as const

export default function About() {
  const { t } = useTranslation(['about', 'products', 'common'])
  const stats = t('stats', { returnObjects: true }) as { value: string; label: string }[]

  return (
    <>
      <Helmet>
        <title>{`${t('meta.title')} | Tdrem`}</title>
        <meta name="description" content={t('meta.description')} />
        <link rel="canonical" href="https://www.tdrem.com/about" />
      </Helmet>

      {/* Intro */}
      <section className="relative -mt-16 overflow-hidden pt-16">
        <div aria-hidden className="bg-grid absolute inset-0 -z-10" />
        <div className="container grid grid-cols-1 items-center gap-10 py-12 sm:py-16 md:py-24 lg:grid-cols-[1.2fr_1fr] lg:gap-12">
          <div className="space-y-5 sm:space-y-6">
            <span className="eyebrow">{t('eyebrow')}</span>
            <h1 className="text-3xl font-extrabold leading-[1.12] sm:text-4xl md:text-5xl">{t('title')}</h1>
            <p className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg md:text-xl">{t('intro')}</p>
          </div>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {stats.map((s) => (
              <div key={s.label} className="surface px-2.5 py-4 text-center sm:p-5">
                <div dir="ltr" className="text-gradient-brand text-2xl font-extrabold sm:text-3xl md:text-4xl">
                  {s.value}
                </div>
                <div className="mt-1 text-xs leading-snug text-muted-foreground">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Think · Develop · REMaster */}
      <section className="border-y bg-muted/40">
        <div className="container py-16 md:py-24">
          <Reveal>
            <SectionHeader eyebrow="Think · Develop · REMaster" title={t('approach.title')} subtitle={t('approach.subtitle')} />
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-5 sm:mt-12 md:grid-cols-3">
            {PILLARS.map(({ key, icon: Icon }, i) => (
              <Reveal key={key} delay={i * 80}>
                <div className="surface h-full p-6 sm:p-7">
                  <div className="mb-5 flex items-center justify-between">
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-brand-blue to-brand-cyan text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <span className="text-5xl font-extrabold text-muted-foreground/15">0{i + 1}</span>
                  </div>
                  <h3 className="mb-2 text-xl font-bold">{t(`approach.${key}.title`)}</h3>
                  <p className="leading-relaxed text-muted-foreground">{t(`approach.${key}.description`)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Products we run */}
      <section className="container py-16 md:py-24">
        <Reveal>
          <SectionHeader eyebrow={t('portfolio.eyebrow')} title={t('portfolio.title')} />
        </Reveal>
        <div className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-3">
          {PRODUCTS.map((p, i) => (
            <Reveal key={p.id} delay={i * 50}>
              <Link
                to={productPath(p.slug)}
                className="surface group flex items-center gap-3 p-3.5 transition-shadow hover:shadow-lift sm:gap-4 sm:p-4"
              >
                <ProductIconTile product={p} />
                <span className="min-w-0 flex-1">
                  <span className="block font-semibold">{t(`products:${p.id}.name`)}</span>
                  <span className="block line-clamp-2 text-sm text-muted-foreground sm:truncate">
                    {t(`products:${p.id}.menuLine`)}
                  </span>
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5 rtl:group-hover:-translate-x-0.5 flip-rtl" />
              </Link>
            </Reveal>
          ))}
        </div>
        <div className="mt-8 text-center">
          <Link to={ROUTES.PRODUCTS} className="text-sm font-medium text-primary hover:underline">
            {t('common:actions.viewAll')}
          </Link>
        </div>
      </section>

      <CtaBand title={t('cta.title')} subtitle={t('cta.subtitle')} />
    </>
  )
}
